import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { DOCUMENT, isPlatformBrowser, isPlatformServer } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  HostListener,
  Inject,
  OnDestroy,
  OnInit,
  Optional,
  PLATFORM_ID,
  ViewChild,
} from '@angular/core';
import { Meta } from '@angular/platform-browser';
import { ActivatedRoute } from '@angular/router';
import { EventTrackerService } from '@commons/tracker/event-tracker/event-tracker.service';
import { NotificationService } from '@core/services/notification.service';
import { SeoService } from '@core/services/seo.service';
import { langFromLocale } from '@core/utils/utils';
import { environment } from '@environment';
import { Links, Metas } from '@features/jobs-unlogged/seo-vars';
import { ViewAnimation } from '@features/jobs/view/view.animation';
import { LocaleEnum } from '@models/commons/locales.enum';
import { Job } from '@models/job/job';
import { REQUEST } from '@nguniversal/express-engine/tokens';
import { Dispatch } from '@ngxs-labs/dispatch-decorator';
import { Store } from '@ngxs/store';
import { LinkRel, TypesEnum } from '@routing/directories/directories-routing.enum';
import { JobsState } from '@store/job/jobs.state';
import { BookmarkToggle } from '@store/profile/bookmark/bookmark.actions';
import { WINDOW } from '@wizbii/angular-utilities';
import { Request } from 'express';
import { BehaviorSubject, Observable } from 'rxjs';
import { distinctUntilChanged, filter, map, shareReplay, startWith, switchMap, take, tap } from 'rxjs/operators';

@Component({
  selector: 'app-view',
  templateUrl: './view.component.html',
  styleUrls: ['./view.component.scss'],
  animations: [...ViewAnimation],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ViewComponent implements OnInit, OnDestroy {
  @ViewChild('globalContainer') globalContainer: ElementRef;

  job$: Observable<Job>;
  showFooter: boolean;

  locale = environment.locale;
  LocaleEnum = LocaleEnum;

  private metaElements: HTMLMetaElement[] = [];

  wizbiiDomain = environment.apiDomain;
  showMedia = false;

  accountUrlSignup: string;

  applyButtonFixed$ = new BehaviorSubject<boolean>(true);

  readonly isMobile$: Observable<boolean>;

  constructor(
    private readonly seoService: SeoService,
    private readonly metaService: Meta,
    private readonly eventTrackerService: EventTrackerService,
    private readonly notificationService: NotificationService,
    route: ActivatedRoute,
    @Inject(WINDOW) private readonly window: any,
    @Inject(DOCUMENT) private readonly document: any,
    store: Store,
    @Optional() @Inject(REQUEST) private readonly request: Request,
    @Inject(PLATFORM_ID) private readonly platformId: Record<string, unknown>,
    private readonly breakpointObserver: BreakpointObserver
  ) {
    this.showMedia = isPlatformBrowser(this.platformId);

    this.isMobile$ = this.breakpointObserver.observe([Breakpoints.Handset]).pipe(
      startWith({ matches: true }),
      map((result) => result.matches),
      distinctUntilChanged(),
      shareReplay({ bufferSize: 1, refCount: true })
    );

    this.job$ = route.params.pipe(
      map((params) => params['jobId']),
      distinctUntilChanged(),
      switchMap((jobId) => store.select(JobsState.job(jobId))),
      tap((job) => {
        if (job) {
          this.pushNotification(job);
        }
      }),
      shareReplay()
    );
  }

  ngOnInit(): void {
    const redirect =
      this.request && isPlatformServer(this.platformId)
        ? `${this.request.protocol}://${this.request.get('host')}${this.request.url}`
        : `${this.window.location.origin}${this.window.location.pathname}`;

    this.accountUrlSignup = `${environment.urls.accountSignup}${redirect}`;
    this.job$
      .pipe(
        filter((job) => !!job),
        take(1)
      )
      .subscribe((job) => {
        this.cleanSeo();
        const lang = langFromLocale(job.locale);
        this.seoService.createLinkForCanonicalURL(lang, TypesEnum.job, {
          companyId: job.company.id,
          jobId: job.id,
        });
        this.seoService.addLinkTags(Links);

        this.metaElements = [
          this.metaService.addTag({
            name: 'author',
            content: job.company.name,
          }),
          this.metaService.addTag({
            itemprop: 'name',
            content: job.title,
          }),
          this.metaService.addTag({
            name: 'twitter:image:src',
            content: `https://images.wizbii.com/company/logo?slug=${job.company.id}&size=large@2x`,
          }),
          this.metaService.addTag({
            property: 'og:url',
            content: `https://www.wizbii.com/company/${job.company.id}/job/${job.id}`,
          }),
          this.metaService.addTag({
            property: 'og:image',
            content: `https://images.wizbii.com/company/logo?slug=${job.company.id}&size=large@2x`,
          }),
          this.metaService.addTag({
            property: 'article:author',
            content: job.company.name,
          }),
          this.metaService.addTag({
            property: 'article:publisher',
            content: job.company.name,
          }),
          this.metaService.addTag({
            itemprop: 'image',
            content: `https://images.wizbii.com/company/logo?slug=${job.company.id}&size=large@2x`,
          }),
          ...this.metaService.addTags(Metas),
        ];
      });
  }

  @HostListener('window:scroll') onScroll(): void {
    const globalContainerBounding = this.globalContainer.nativeElement.getBoundingClientRect();
    if (
      +globalContainerBounding.bottom + 74 >
      (this.window.innerHeight || this.document.documentElement.clientHeight)
    ) {
      this.applyButtonFixed$.next(false);
    } else {
      this.applyButtonFixed$.next(true);
    }
  }

  @Dispatch() toggleBookmark = (job: Job): BookmarkToggle => {
    this.eventTrackerService.send({
      eventCategory: 'Job Page',
      eventAction: 'Click Save',
      eventLabel: 'Unlogged',
    });

    return new BookmarkToggle(job.id);
  };

  getCity(job: Job): string {
    if (!job || !job.location) {
      return undefined;
    }

    return job.location.city
      ? job.location.city
      : job.location.department
      ? job.location.department
      : job.location.country
      ? job.location.country
      : undefined;
  }

  ngOnDestroy(): void {
    this.applyButtonFixed$.complete();
    this.cleanSeo();
  }

  private pushNotification({ contract }: Job) {
    if (contract && contract.id === 'alternance') {
      const notificationID = 'la-ruche-alternance';

      this.notificationService.push({
        id: notificationID,
        product: 'La Ruche',
        title: 'Comment convaincre une entreprise de me prendre en alternance ?',
        content: 'Les arguments à lui exposer !',
        callToAction: "Lire l'article",
        // link's utm_medium is inserted directly by interservices domain components.
        link:
          `https://laruche.wizbii.com/recruter-des-alternants-n-est-pas-aussi-contraignant-qu-on-ne-le-pense/` +
          `?utm_source=wizbii&utm_label=${notificationID}`,
      });
    }

    if (contract && (contract.id === 'alternance' || contract.id === 'stage')) {
      const notificationID = 'fibii-stage-alternance';

      this.notificationService.push({
        id: notificationID,
        product: 'Fibii',
        title: 'Financez vos études !',
        content: "Près de 10 000€ d'aides sont disponibles par an.",
        callToAction: 'Calculer mes aides',
        // link's utm_medium is inserted directly by interservices domain components.
        link: `https://www.fibii.co?utm_source=wizbii&utm_label=${notificationID}`,
      });
    }
  }

  private cleanSeo() {
    this.seoService.removeLinkWithRel(LinkRel.Canonical);

    Links.forEach((link) => {
      this.seoService.removeLinkTag(link);
    });
    this.metaElements.forEach((meta) => {
      this.metaService.removeTagElement(meta);
    });
    this.metaElements = [];
  }
}
